import {
  bookingDateInMonth,
  defaultBookingDateForMonth,
  exitYearMonthFromDate,
  monthIndexFromYearMonth,
  yearMonthFromYmd,
  yearMonthsInRange,
} from './payrollAdjustmentDates';
import type {
  PayrollAdjustmentCategory,
  PayrollAdjustmentEntryRow,
  PayrollAdjustmentMonthRow,
  PayrollAdjustmentRuleRow,
} from './payrollAdjustmentTypes';

export type PayrollAdjustmentEntryLike = Pick<
  PayrollAdjustmentEntryRow,
  'employee_id' | 'category_key' | 'year_month' | 'booking_date' | 'amount'
> & Partial<Pick<PayrollAdjustmentEntryRow, 'id' | 'note' | 'employee_name'>>;

export type PayrollAdjustmentRuleLike = Pick<
  PayrollAdjustmentRuleRow,
  | 'employee_id'
  | 'category_key'
  | 'amount'
  | 'start_year_month'
  | 'end_mode'
  | 'end_year_month'
  | 'day_of_month'
  | 'active'
> & Partial<Pick<PayrollAdjustmentRuleRow, 'id' | 'employee_name' | 'note'>> & {
  employee_end_date?: string | null;
};

export type BuildPayrollAdjustmentMonthRowsOptions = {
  /** When true (default), inactive rules emit no rows. Employee history passes false. */
  requireActiveRules?: boolean;
};

function isOccurrenceInRange(
  bookingDate: string,
  fromYmd: string,
  toYmd: string,
  employeeEndDate: string | null | undefined,
): boolean {
  if (bookingDate < fromYmd || bookingDate > toYmd) return false;
  const exitYmd = employeeEndDate?.slice(0, 10);
  if (exitYmd && bookingDate > exitYmd) return false;
  return true;
}

export function isRuleOccurrenceInMonth(
  rule: Pick<
    PayrollAdjustmentRuleRow,
    'start_year_month' | 'end_mode' | 'end_year_month' | 'active'
  >,
  yearMonth: string,
  employeeEndDate: string | null | undefined,
  requireActiveRules: boolean,
): boolean {
  if (requireActiveRules && !rule.active) return false;
  const curIdx = monthIndexFromYearMonth(yearMonth);
  const startIdx = monthIndexFromYearMonth(rule.start_year_month);
  if (curIdx < startIdx) return false;
  if (rule.end_mode === 'date' && rule.end_year_month) {
    return curIdx <= monthIndexFromYearMonth(rule.end_year_month);
  }
  const exitYm = exitYearMonthFromDate(employeeEndDate);
  if (exitYm && curIdx > monthIndexFromYearMonth(exitYm)) return false;
  return true;
}

/** @deprecated Use isRuleOccurrenceInMonth with requireActiveRules=true. */
export function isRuleActiveInMonth(
  rule: Pick<
    PayrollAdjustmentRuleRow,
    'start_year_month' | 'end_mode' | 'end_year_month' | 'active'
  >,
  yearMonth: string,
  employeeEndDate: string | null | undefined,
): boolean {
  return isRuleOccurrenceInMonth(rule, yearMonth, employeeEndDate, true);
}

function ruleHistoryEndYearMonth(
  rule: Pick<PayrollAdjustmentRuleRow, 'end_mode' | 'end_year_month'>,
  employeeEndDate: string | null | undefined,
  capYearMonth: string,
): string {
  let endYm = capYearMonth;
  if (rule.end_mode === 'date' && rule.end_year_month) {
    endYm = rule.end_year_month < capYearMonth ? rule.end_year_month : capYearMonth;
  } else {
    const exitYm = exitYearMonthFromDate(employeeEndDate);
    if (exitYm && exitYm < endYm) endYm = exitYm;
  }
  return endYm;
}

function collectHistoryYearMonths(
  entries: PayrollAdjustmentEntryLike[],
  rules: PayrollAdjustmentRuleLike[],
  employeeEndDate: string | null | undefined,
  capYearMonth: string,
): string[] {
  const months = new Set<string>();

  for (const entry of entries) {
    months.add(yearMonthFromYmd(entry.booking_date ?? `${entry.year_month}-15`));
  }

  for (const rule of rules) {
    const endYm = ruleHistoryEndYearMonth(rule, employeeEndDate, capYearMonth);
    if (rule.start_year_month > endYm) continue;
    const rangeEnd = bookingDateInMonth(endYm, 31);
    for (const ym of yearMonthsInRange(`${rule.start_year_month}-01`, rangeEnd)) {
      months.add(ym);
    }
  }

  return [...months].sort();
}

export function buildPayrollAdjustmentMonthRows(
  entries: PayrollAdjustmentEntryLike[],
  rules: PayrollAdjustmentRuleLike[],
  categories: PayrollAdjustmentCategory[],
  yearMonth: string,
  employeeNameById: Map<string, string>,
  employeeEndDateById: Map<string, string | null>,
  options: BuildPayrollAdjustmentMonthRowsOptions = {},
): PayrollAdjustmentMonthRow[] {
  const requireActiveRules = options.requireActiveRules !== false;
  const catType = new Map(categories.map((c) => [c.key, c.type]));
  const out: PayrollAdjustmentMonthRow[] = [];

  for (const e of entries) {
    const entryYm = e.booking_date ? yearMonthFromYmd(e.booking_date) : e.year_month;
    if (entryYm !== yearMonth) continue;
    const booking_date = e.booking_date ?? defaultBookingDateForMonth(e.year_month, 15);
    out.push({
      id: e.id ?? `${e.employee_id}-${entryYm}-${e.category_key}`,
      entry_id: e.id ?? null,
      rule_id: null,
      employee_id: e.employee_id,
      employee_name: e.employee_name ?? employeeNameById.get(e.employee_id) ?? '—',
      category_key: e.category_key,
      booking_date,
      amount: e.amount,
      note: e.note ?? '',
      recurring: false,
      type: catType.get(e.category_key) ?? 'abzug',
    });
  }

  for (const r of rules) {
    const endDate = r.employee_end_date ?? employeeEndDateById.get(r.employee_id) ?? null;
    if (!isRuleOccurrenceInMonth(r, yearMonth, endDate, requireActiveRules)) continue;
    const booking_date = bookingDateInMonth(yearMonth, r.day_of_month ?? 15);
    if (endDate && booking_date > endDate.slice(0, 10)) continue;
    out.push({
      id: `rule-${r.id ?? r.employee_id}-${yearMonth}`,
      entry_id: null,
      rule_id: r.id ?? null,
      employee_id: r.employee_id,
      employee_name: r.employee_name ?? employeeNameById.get(r.employee_id) ?? '—',
      category_key: r.category_key,
      booking_date,
      amount: r.amount,
      note: r.note ?? '',
      recurring: true,
      type: catType.get(r.category_key) ?? 'abzug',
    });
  }

  out.sort((a, b) => {
    const dateCmp = a.booking_date.localeCompare(b.booking_date);
    if (dateCmp !== 0) return dateCmp;
    return a.employee_name.localeCompare(b.employee_name, 'de');
  });

  return out;
}

/** Gesamthistorie eines Mitarbeiters (Einzelbuchungen + Regel-Vorkommen), neueste zuerst. */
export function buildPayrollAdjustmentEmployeeHistoryRows(
  entries: PayrollAdjustmentEntryLike[],
  rules: PayrollAdjustmentRuleLike[],
  categories: PayrollAdjustmentCategory[],
  employeeName: string,
  employeeEndDate: string | null | undefined,
): PayrollAdjustmentMonthRow[] {
  const employeeId = entries[0]?.employee_id ?? rules[0]?.employee_id ?? '';
  const employeeNameById = new Map(employeeId ? [[employeeId, employeeName]] : []);
  const employeeEndDateById = new Map(employeeId ? [[employeeId, employeeEndDate ?? null]] : []);
  const nowYm = new Date().toISOString().slice(0, 7);
  const months = collectHistoryYearMonths(entries, rules, employeeEndDate, nowYm);

  const seen = new Set<string>();
  const out: PayrollAdjustmentMonthRow[] = [];

  for (const yearMonth of months) {
    const monthRows = buildPayrollAdjustmentMonthRows(
      entries,
      rules,
      categories,
      yearMonth,
      employeeNameById,
      employeeEndDateById,
      { requireActiveRules: false },
    );
    for (const row of monthRows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }

  out.sort((a, b) => b.booking_date.localeCompare(a.booking_date));
  return out;
}

/** Netto-Summe Be-/Abzüge pro Mitarbeiter in einem Datumsbereich. */
export function sumPayrollAdjustmentNetByEmployee(
  entries: PayrollAdjustmentEntryLike[],
  rules: PayrollAdjustmentRuleLike[],
  categories: PayrollAdjustmentCategory[],
  fromYmd: string,
  toYmd: string,
  employeeEndDateById: Map<string, string | null>,
): Map<string, number> {
  const yearMonths = yearMonthsInRange(fromYmd, toYmd);
  const netByEmployee = new Map<string, number>();
  const emptyNameMap = new Map<string, string>();

  for (const yearMonth of yearMonths) {
    const monthRows = buildPayrollAdjustmentMonthRows(
      entries,
      rules,
      categories,
      yearMonth,
      emptyNameMap,
      employeeEndDateById,
    );
    for (const row of monthRows) {
      const endDate = employeeEndDateById.get(row.employee_id) ?? null;
      if (!isOccurrenceInRange(row.booking_date, fromYmd, toYmd, endDate)) continue;
      const delta = row.type === 'bezug' ? row.amount : -row.amount;
      netByEmployee.set(row.employee_id, (netByEmployee.get(row.employee_id) ?? 0) + delta);
    }
  }

  return netByEmployee;
}
